import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button, Input, Select, Icon, H2, toast, useGraphQLMutation } from '@tviklax/ui-components';
import { Section, FormGrid, FormGroup } from '../../create-order/components/styles';
import { MaerskQuotes } from '../../create-order/components/MaerskQuotes';
import { OrderFormData } from '../../create-order/components/types';
import {
  UPDATE_ORDER_SHIPMENT_MUTATION,
  UpdateOrderShipmentData,
  UpdateOrderShipmentVariables,
} from '../graphql/updateOrderShipment.mutation';

export interface BookShipmentProps {
  /** Numeric id of the row in `order_shipments` (NOT the order id). */
  orderShipmentId: number;
  /** Human-readable label for toasts, e.g. "ORD-71". */
  orderLabel?: string;
  pickupCity?: string;
  pickupCountry?: string;
  deliveryCity?: string;
  deliveryCountry?: string;
  initialCarrier?: string;
  initialPackageType?: string;
  initialWeight?: number | string | null;
  initialDimensions?: string;
  initialEstimatedArrivalDate?: string;
  onClose: () => void;
  /** Called after a successful booking save, before onClose. Use to refetch shipment lists. */
  onBooked?: () => void;
}

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

const SelectedCarrierBanner = styled.div`
  margin-top: 16px;
  padding: 10px 14px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  font-size: 13px;
  color: #9a3412;
`;

export const BookShipment: React.FC<BookShipmentProps> = ({
  orderShipmentId,
  orderLabel,
  pickupCity = '',
  pickupCountry = '',
  deliveryCity = '',
  deliveryCountry = '',
  initialCarrier,
  initialPackageType = '',
  initialWeight,
  initialDimensions = '',
  initialEstimatedArrivalDate = '',
  onClose,
  onBooked,
}) => {
  const [packageType, setPackageType] = useState(initialPackageType);
  const [weight, setWeight] = useState(initialWeight != null ? String(initialWeight) : '');
  const [dimensions, setDimensions] = useState(initialDimensions);
  const [estimatedArrivalDate, setEstimatedArrivalDate] = useState(initialEstimatedArrivalDate);
  const [carrier, setCarrier] = useState(initialCarrier || '');
  const [submitting, setSubmitting] = useState(false);

  const [updateOrderShipment] = useGraphQLMutation<UpdateOrderShipmentData, UpdateOrderShipmentVariables>(
    UPDATE_ORDER_SHIPMENT_MUTATION
  );

  const handlePackageFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'packageType') setPackageType(value);
    if (name === 'weight') setWeight(value);
    if (name === 'dimensions') setDimensions(value);
  };

  // MaerskQuotes expects a full OrderFormData — build a minimal stub carrying only
  // the fields it actually reads (customer/delivery city+country, weight, packageType),
  // matching the exact shape without reusing/duplicating any Create Order UI.
  const maerskFormData: OrderFormData = useMemo(() => ({
    CustomerName: '',
    CustomerEmail: '',
    CustomerContactNumber: '',
    customerAddr1: '',
    customerCity: pickupCity,
    customerState: '',
    customerCountry: pickupCountry,
    customerZip: '',
    VendorName: '',
    VendorEmail: '',
    VendorContactNumber: '',
    vendorAddr1: '',
    vendorCity: '',
    vendorState: '',
    vendorCountry: '',
    vendorZip: '',
    SalesPerson: '',
    pickupAddr1: '',
    pickupCity,
    pickupState: '',
    pickupCountry,
    pickupZip: '',
    deliveryAddr1: '',
    deliveryCity,
    deliveryState: '',
    deliveryCountry,
    deliveryZip: '',
    packageType,
    weight,
    dimensions,
    deliveryDate: estimatedArrivalDate,
    remittanceTranches: [],
    specialInstructions: '',
    items: [],
    subtotal: 0,
    taxPercent: 0,
    taxAmount: 0,
    discountPercent: 0,
    discountAmount: 0,
    total: 0,
    currency: 'USD',
    insuranceRequired: false,
    customsDocumentsRequired: false,
    priorityLevel: 'Normal',
  }), [pickupCity, pickupCountry, deliveryCity, deliveryCountry, packageType, weight, dimensions, estimatedArrivalDate]);

  const handleMaerskSelect = (quote: { service: string; id: string; price: number; currency: string }) => {
    setCarrier(`Maersk – ${quote.service}`);
    toast({
      title: 'Maersk Rate Selected',
      description: `${quote.service} at ${quote.currency} ${quote.price.toLocaleString()} selected for this shipment.`,
    });
  };

  const isBlank = (value?: string) => !value || value.trim().length === 0;

  const handleSubmit = async () => {
    if (isBlank(packageType) || !weight || Number(weight) <= 0) {
      toast({
        title: 'Package details required',
        description: 'Select package type and provide a valid total weight.',
        variant: 'destructive',
      });
      return;
    }

    if (isBlank(carrier)) {
      toast({
        title: 'Carrier rate required',
        description: 'Fetch rates and select a Maersk service before booking.',
        variant: 'destructive',
      });
      return;
    }

    if (isBlank(estimatedArrivalDate)) {
      toast({
        title: 'Delivery date required',
        description: 'Set an expected delivery date before booking.',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    try {
      const { data, errors } = await updateOrderShipment({
        variables: {
          input: {
            orderShipmentId,
            carrier,
            packageType,
            weight: Number(weight),
            dimensions,
            estimatedArrivalDate,
          },
        },
      });

      if (data?.updateOrderShipment) {
        toast({
          title: '✅ Shipment Booked',
          description: `Booking details saved for ${orderLabel || `shipment #${orderShipmentId}`}.`,
        });
        onBooked?.();
        onClose();
      } else {
        toast({
          title: '❌ Failed to Save Booking',
          description: errors?.[0]?.message || 'Please check your inputs and try again.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({
        title: '❌ Failed to Save Booking',
        description: error?.message || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Section>
        <H2 icon={<Icon name="Package" size={20} color="#f86400" />}>Package Details</H2>
        <FormGrid>
          <FormGroup $span={3}>
            <Select
              label="Package Type"
              name="packageType"
              value={packageType}
              onChange={handlePackageFieldChange}
              items={[
                { value: '', label: 'Select Type' },
                { value: 'box', label: 'Box / Carton' },
                { value: 'pallet', label: 'Pallet' },
                { value: 'crate', label: 'Crate' },
                { value: 'container', label: 'Container' },
                { value: 'envelope', label: 'Envelope' },
              ]}
              required
            />
          </FormGroup>
          <FormGroup $span={3}>
            <Input
              label="Total Weight (kg)"
              type="number"
              name="weight"
              value={weight}
              onChange={handlePackageFieldChange}
              placeholder="0.00"
              required
            />
          </FormGroup>
          <FormGroup $span={6}>
            <Input
              label="Dimensions (L × W × H cm)"
              type="text"
              name="dimensions"
              value={dimensions}
              onChange={handlePackageFieldChange}
              placeholder="e.g., 120 × 100 × 110"
            />
          </FormGroup>
        </FormGrid>
      </Section>

      <MaerskQuotes formData={maerskFormData} onSelect={handleMaerskSelect} />

      {carrier && (
        <SelectedCarrierBanner>
          Selected carrier: <strong>{carrier}</strong>
        </SelectedCarrierBanner>
      )}

      <Section style={{ marginTop: '24px' }}>
        <H2 icon={<Icon name="Calendar" size={20} color="#f86400" />}>Delivery Date</H2>
        <FormGrid>
          <FormGroup $span={4}>
            <Input
              label="Expected Delivery Date"
              type="date"
              name="estimatedArrivalDate"
              value={estimatedArrivalDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEstimatedArrivalDate(e.target.value)}
              required
            />
          </FormGroup>
        </FormGrid>
      </Section>

      <Footer>
        <Button variant="outline" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Saving...' : 'Confirm Booking'}
        </Button>
      </Footer>
    </div>
  );
};
